import React, { useState } from 'react';
import axios from 'axios';

const BLUE = '#00388A';

const sections = [
  { id: 'A', title: "Why do I engage in independent study?", questions: [
    { id: 1, text: "Because I want my professors to think I am a dedicated student." },
    { id: 2, text: "Because I'll fall behind or fail if I don't do it." },
    { id: 3, text: "Because I find the material genuinely fascinating." },
    { id: 4, text: "Because I would feel guilty or like a failure if I didn't study." },
    { id: 5, text: "Because I truly want to master the subject matter." },
    { id: 6, text: "Because that is what is expected of a university student." },
    { id: 7, text: "Because I enjoy the process of learning and researching." },
    { id: 8, text: "Because deep-diving into these topics is important to me." }
  ]},
  { id: 'B', title: "Why do I stay focused during lectures?", questions: [
    { id: 9, text: "So that I don't look disrespectful to the professor." },
    { id: 10, text: "Because I want to make a good impression on the faculty." },
    { id: 11, text: "Because I want to gain new insights and perspectives." },
    { id: 12, text: "Because I'd feel ashamed if I wasted the tuition or the opportunity." },
    { id: 13, text: "Because the lectures are intellectually stimulating." },
    { id: 14, text: "Because attending and paying attention is the rule." },
    { id: 15, text: "Because I enjoy being part of the academic discussion." },
    { id: 16, text: "Because it's important to me to fully grasp the lecture content." }
  ]},
  { id: 'C', title: "Why do I participate in class discussions?", questions: [
    { id: 17, text: "Because I want my peers to perceive me as intelligent." },
    { id: 18, text: "Because I'd feel like I'm not contributing enough if I stay silent." },
    { id: 19, text: "Because I enjoy tackling complex academic questions." },
    { id: 20, text: "Because active participation is part of the required engagement." },
    { id: 21, text: "To test my understanding and see if my reasoning is correct." },
    { id: 22, text: "Because debating difficult concepts is rewarding." },
    { id: 23, text: "Because it's important to me to challenge my own ideas in public." },
    { id: 24, text: "Because I want the professor to recognize my merit." }
  ]},
  { id: 'D', title: "Why do I strive to achieve high grades?", questions: [
    { id: 25, text: "Because achieving excellence is what I am here for." },
    { id: 26, text: "So that my professors and the university see me as a top student." },
    { id: 27, text: "Because I take pride in the quality of my academic work." },
    { id: 28, text: "Because I'm afraid of the consequences of a low GPA." },
    { id: 29, text: "Because I would feel like I've let myself down if I didn't excel." },
    { id: 30, text: "Because reaching my academic goals is personally meaningful." },
    { id: 31, text: "Because I feel a great sense of personal accomplishment when I succeed." },
    { id: 32, text: "Because a high GPA might lead to better career opportunities or rewards." }
  ]}
];

const scaleOptions = [
  { val: 1, label: "Not at all true" },
  { val: 2, label: "Not so true" },
  { val: 3, label: "Sort of true" },
  { val: 4, label: "Very true" }
];

const SRQAForm = ({ userHash, onComplete }) => {
  const [answers, setAnswers] = useState({});

  const handleSelect = (qId, val) => {
    setAnswers({ ...answers, [qId]: val });
  };

  const handleSubmit = async () => {
    const filled = {};
    for (const section of sections) {
      for (const q of section.questions) {
        filled[q.id] = answers[q.id] !== undefined ? answers[q.id] : 1;
      }
    }

    const payload = {
      student_id: userHash || "STUDENT_TEST_001",
      responses: filled
    };

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const res = await axios.post(`${baseUrl}/api/assessment/srqa`, payload);
      onComplete(res.data);
    } catch (err) {
      console.error("Submission Error:", err.response?.data);
      if (err.response?.status === 409) {
        // questionario ancora bloccato lato server (finestra di 28 giorni)
        alert(err.response.data?.detail || "The questionnaires are not available yet.");
      } else {
        alert("Something went wrong. Please try again later.");
      }
    }
  };

  let questionCounter = 0;

  return (
    <div style={formCardStyle}>
      <header style={{ marginBottom: '30px', textAlign: 'center' }}>
        <p style={stepLabelStyle}>Questionnaire 3/3</p>
        <p style={introTextStyle}>Think about your academic motivation. Please answer the following questions:</p>
      </header>

      {sections.map(section => (
        <div key={section.id} style={{ marginBottom: '40px' }}>
          <p style={sectionHeaderStyle}>{section.title}</p>
          {section.questions.map(q => {
            questionCounter++;
            return (
              <div key={q.id} style={questionRowStyle}>
                <p style={questionTextStyle}>
                  <span style={numberStyle}>{questionCounter}.</span> {q.text}
                </p>
                <div style={optionsContainerStyle}>
                  {scaleOptions.map((opt) => (
                    <button
                      key={opt.val}
                      onClick={() => handleSelect(q.id, opt.val)}
                      style={{
                        ...optionButtonStyle,
                        backgroundColor: answers[q.id] === opt.val ? BLUE : 'transparent',
                        color: answers[q.id] === opt.val ? 'white' : '#555',
                        borderColor: answers[q.id] === opt.val ? BLUE : '#DDD',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <button onClick={handleSubmit} style={submitButtonStyle}>Continue</button>
      </div>
    </div>
  );
};

const stepLabelStyle = { fontSize: '13px', color: BLUE, fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '15px' };
const formCardStyle = { maxWidth: '700px', width: '100%', backgroundColor: '#FFFFFF', padding: '50px 40px', borderRadius: '20px', boxShadow: '0 2px 15px rgba(0,0,0,0.03)' };
const introTextStyle = { fontSize: '18px', lineHeight: '1.6', color: '#444', marginBottom: '20px', textAlign: 'center' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: '600', color: BLUE, marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #F5F5F5' };
const questionRowStyle = { textAlign: 'left', borderBottom: '1px solid #F5F5F5', paddingBottom: '15px', marginBottom: '15px', display: 'flex', flexDirection: 'column' };
const questionTextStyle = { fontSize: '15.5px', color: '#333', marginBottom: '12px', lineHeight: '1.5' };
const numberStyle = { color: BLUE, fontWeight: '600', marginRight: '8px' };
const optionsContainerStyle = { display: 'flex', gap: '10px', flexWrap: 'wrap' };
const optionButtonStyle = { padding: '8px 14px', borderRadius: '20px', border: '1px solid', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s ease', fontWeight: '500' };
const submitButtonStyle = { backgroundColor: BLUE, color: 'white', padding: '15px 50px', border: 'none', borderRadius: '50px', fontSize: '16px', fontWeight: '600', letterSpacing: '0.5px', transition: 'all 0.3s ease', cursor: 'pointer' };

export default SRQAForm;